import Drawer from 'components/common/Drawer';
import Footer from './Footer';
import BasicInformation from './BasicInformation';
import { getDatasourceOverrides } from 'institutionalAdmin/actions/institutions';
import {
    getUsableStudyTypesByInstitution,
    getStudyMetadata,
    getStudyStages,
    saveStudy
} from 'pages/study/actions/createStudyV2';
import { browserHistory } from 'react-router';
import {
    loadStudy,
    updateStudy,
} from 'pages/study/actions/editStudy';
import { getStudy, unloadStudy } from 'pages/studyOverview/actions/study.js';
import { connect } from 'react-redux';
import { useEffect, useState, useMemo, useRef } from 'react';
import { get, filter, map, isEmpty, unionBy, isNil } from 'lodash';
import { getOverride, getValue } from 'common/util/binderHelpers';
import { dataSourceDataTypeSelector } from 'pages/dataSource/selectors';
import BinderDetails from './BinderDetails';
import {
    loadDataSourceList
} from 'pages/dataSource/actions/dataSource';
import { initializeStudyWorkflow, setField } from 'pages/study/actions/workflow';
import { getBinderV4Essentials } from 'pages/study/utils/studyUtils';
import { updateDraft } from 'pages/study/actions/updateDraft';

const DRAFT_MODE = 'draft';
const ACTIVATE_MODE = 'activate';

const INIT_STATE = {
    basicInformation: {
        studyType: '',
        sponsorCro: '',
        trialId: '',
        studyName: '',
        studyStage: ''
    },
    notValidFields: {
        studyType: false,
        sponsorCro: false,
        trialId: false,
        studyName: false,
        studyStage: false
    }
};

function CreateBinder({
    isOpen,
    dataType, studyDetails,
    studyStages,
    selectedDatasourceOverrides,
    getStudyStages,
    getStudyMetadata,
    dataSources,
    canAddDataSource,
    handleOnClose,
    getUsableStudyTypesByInstitution,
    institutionId,
    binderTypes,
    binderTemplates,
    getDatasourceOverrides,
    loadDataSourceList,
    initializeStudyWorkflow,
    setField,
    rowData,
    getStudy,
    loadStudy,
    draftStudyData,
    study,
    unloadStudy,
    ...props }) {

    const [basicInformation, setBasicInformation] = useState(INIT_STATE.basicInformation);
    const [notValidsFields, setNotValidFields] = useState(INIT_STATE.notValidFields);

    const [initiallyValidated, setInitiallyValidated] = useState(false);
    const [isStudyBinder, setIsStudyBinder] = useState(false);
    const [restOfStudyDetails, setRestOfStudyDetails] = useState([]);
    const [isStep1, setIsStep1] = useState(true);
    const [isStep2, setIsStep2] = useState(false);
    const [isDelegationOfAuthorityOn, setIsDelegationOfAuthority] = useState(true);
    const [isEventOn, setIsEventOn] = useState(true);
    const [binderEssentials, setBinderEssentials] = useState([]);
    const [isSubmiting, setIsSubmiting] = useState(null);
    const pendingAction = useRef(null);

    const isStudyLoading = get(study, 'loading', false);

    useEffect(() => {
        if (!isStudyLoading && pendingAction.current) {
            const action = pendingAction.current;
            pendingAction.current = null;
            action();
        }
    }, [isStudyLoading]);

    useEffect(() => {
        if (!rowData || !rowData.id) return;
        // This is a draft study
        const { id } = rowData;
        if (id) {
            loadStudy(id);
        }
    }, []);

    useEffect(() => {
        if (isOpen) {
            initializeStudyWorkflow();
            getUsableStudyTypesByInstitution(institutionId);
            loadDataSourceList(institutionId);
            getDatasourceOverrides(institutionId);
            getStudyStages();
            setBinderEssentials(getBinderV4Essentials());
        }
    }, [isOpen]);

    useEffect(() => {
        if (initiallyValidated) {
            isStudyBinder ? validateEmptyFieldsStudyBinder() : validateEmptyFieldsAdminBinder();
        }
    }, [basicInformation]);

    useEffect(() => {
        const selectedBinderType = get(study, 'fields.studyType', {});

        if (selectedBinderType.id) {
            const { id, binderTemplateId, category } = selectedBinderType;
            getStudyMetadata(institutionId, binderTemplateId, id);
            setIsStudyBinder(category === 'study_binder');
        }
    }, [props.isDraft, basicInformation.studyType]);

    useEffect(() => {
        if (!props.isDraft) return;
        const studyType = get(study, 'fields.studyType', '');
        const studyName = get(study, 'fields.studyName', '');
        const studyStage = get(study, 'fields.studyStage', '');

        setBasicInformation({
            ...basicInformation,
            studyType,
            studyName,
            studyStage,
        });
    }, [props.isDraft]);

    useEffect(() => {
        loader();
    }, [studyDetails, isStudyBinder]);
    
    function loader() {
        const existingStudyDetails = get(study, 'fields.studyDetails', []);
        const sponsorOverrider = getOverride('Sponsor', selectedDatasourceOverrides, dataSources);
        const protocolTrialOverrider = getOverride('Protocol Number / Trial id', selectedDatasourceOverrides, dataSources);
        const sponsorValue = getValue('Sponsor', true, study, sponsorOverrider ? sponsorOverrider.name : '') || getValue('Sponsor', false, study, sponsorOverrider ? sponsorOverrider.name : '');
        const protocolValue = getValue('Protocol Number / Trial id', true, study, protocolTrialOverrider ? protocolTrialOverrider.name : '') || getValue('Protocol Number / Trial id', false, study, protocolTrialOverrider ? protocolTrialOverrider.name : '');

        setBasicInformation((prevState) => ({
            ...prevState,
            sponsorCro: sponsorValue,
            trialId: protocolValue
        }));

        let rest = studyDetails.filter(detail => {
            if (sponsorOverrider && !isEmpty(detail) && detail.dataSourceId === sponsorOverrider.id) {
                return false;
            } else if (protocolTrialOverrider && !isEmpty(detail) && detail.dataSourceId === protocolTrialOverrider.id) {
                return false;
            }

            return true;
        });

        rest.map(r => {
            if (!isEmpty(r) && !r.dataSourceId && r.inputFieldType.includes('single')) {
                r.inputFieldType = '';
            }

            existingStudyDetails.map(s => {
                if (!s) return;
                if (r.name === s.name) {
                    r.value = s.value;
                }
            });
        });

        if (rest.length) {
            rest = rest.sort((a, b) => {
                const nameA = a.name.toUpperCase();
                const nameB = b.name.toUpperCase();
                if (nameA < nameB) {
                    return -1;
                }
                if (nameA > nameB) {
                    return 1;
                }

                return 0;
            });
            rest = rest.filter(el => !isEmpty(el) && !isNil(el));
            setRestOfStudyDetails(rest);
        }
    }

    function setStudyEssentials(essential, value, field) {
        const existingEssentials = get(study, 'fields.studyEssentials', []);
        let _value = value;

        if (value && value.text) {
            _value = value.text;
        }

        const essentials = unionBy(existingEssentials, binderEssentials, 'name');

        essentials.map((el) => {
            if (el.name === essential) {
                el.value = _value;
            }
        });

        if (field) {
            setBasicInformation(prev => ({
                ...prev,
                [field]: _value
            }));
        }
        console.log('setStudyEssentials', essentials);
        setField('studyEssentials', essentials);
    }

    function setStudyDetails(detailId, value, field) {
        console.log('setStudyDetails', detailId, value, field);
        const existingStudyDetails = get(study, 'fields.studyDetails', []);
        const details = unionBy(existingStudyDetails, studyDetails, 'name');
        console.log('details', details);
        details.map(el => {
            if (!el) return;
            if (el.descriptorId === detailId) {
                el['value'] = value;
            }
        });

        if (field) {
            setBasicInformation(prev => ({
                ...prev,
                [field]: value
            }));
        }
        setField('studyDetails', details);
    }

    function validateEmptyFieldsStudyBinder() {
        const fields = {
            studyType: isEmpty(basicInformation.studyType),
            sponsorCro: isEmpty(basicInformation.sponsorCro),
            trialId: isEmpty(basicInformation.trialId),
            studyName: isEmpty(basicInformation.studyName),
            studyStage: isEmpty(basicInformation.studyStage)
        };

        setNotValidFields(prev => ({
            ...prev,
            ...fields
        }));
        return !fields.studyType && !fields.sponsorCro && !fields.trialId && !fields.studyName && !fields.studyStage;
    }

    function validateEmptyFieldsAdminBinder() {
        const fields = {
            studyType: isEmpty(basicInformation.studyType),
            studyName: isEmpty(basicInformation.studyName),
            studyStage: isEmpty(basicInformation.studyStage)
        };

        setNotValidFields(prev => ({
            ...prev,
            ...fields
        }));
        return !fields.studyType && !fields.studyName && !fields.studyStage;
    }

    const drawerTitle = useMemo(() => {
        if (props.isDraft) {
            return basicInformation.studyName;
        }
        if (isStep1) {
            return 'Create Binder';
        }

        if (isStep2) {
            return basicInformation.studyName;
        }
    }, [isStep1, isStep2, props.isDraft, basicInformation]);

    function handleNext() {
        setInitiallyValidated(true);
        const isValid = isStudyBinder ? validateEmptyFieldsStudyBinder() : validateEmptyFieldsAdminBinder();

        if (isValid) {
            setIsStep2(true);
            setIsStep1(false);
        }
    }

    function handlePrevious() {
        setIsStep2(false);
        setIsStep1(true);
    }

    function handleOnChange(value, field) {
        if (field === 'studyStage' || field === 'studyType') {
            if (value.name === 'Open to Accrual' || value.name === 'Maintenance') {
                const binderTemplate = binderTemplates.find(template => template.id === basicInformation.studyType.binderTemplateId);
                binderTemplate && binderTemplate.billable === true ? setField('billable', true) : setField('billable', false);
            }
            else {
                setField('billable', false);
            }
        }

        setBasicInformation(prev => ({
            ...prev,
            [field]: value
        }));

        setField(field, value);
    }

    function handleSubmit() {
        setIsSubmiting('submit');
        const { isDraft, saveStudy } = props;

        const doSubmit = () => {
            if (isDraft) {
                return activateStudy(ACTIVATE_MODE);
            }
            return saveStudy(ACTIVATE_MODE, '/').then((studyId) => {
                setIsSubmiting(null);
                handleAfterSave(studyId);
            });
        };

        if (isStudyLoading) {
            pendingAction.current = doSubmit;
        } else {
            doSubmit();
        }
    }

    function handleDraft() {
        setIsSubmiting('draft');
        const { isDraft, saveStudy, updateDraft } = props;

        const doDraft = () => {
            if (isDraft) {
                return updateDraft(rowData.id, '/');
            }
            return saveStudy(DRAFT_MODE, '/').then(() => {
                setIsSubmiting(null);
                handleAfterSave();
            });
        };

        if (isStudyLoading) {
            pendingAction.current = doDraft;
        } else {
            doDraft();
        }
    }

    const CreateBinderFooter = () => {
        return (
            <Footer
                isStep={isStep1 ? 'one' : 'two'}
                handleCancel={handleAfterSave}
                handleNext={handleNext}
                handlePrevious={handlePrevious}
                handleSubmit={handleSubmit}
                handleSaveAsDraft={handleDraft}
                submittingAction={isSubmiting}
            />
        );
    };

    function handleAfterSave(id) {
        unloadStudy();
        handleOnClose();
        setInitiallyValidated(false);
        setIsStep1(true);
        setIsStep2(false);
        setBasicInformation(INIT_STATE.basicInformation);
        setNotValidFields(INIT_STATE.notValidFields);

        if(typeof id === 'string') {
            browserHistory.push(`/study/${id}/dashboard`);
        }
    }

    function handleDOACheck(value) {
        setIsDelegationOfAuthority(value);
        setField('delegationOfAuthority', value);
    }

    function handleEventCheck(value) {
        setIsEventOn(value);
        setField('eventsTabEnabled', value);
    }

    function activateStudy(status) {
        const { updateStudy } = props;
        const existingStudyId = rowData.id;
        updateStudy(existingStudyId, status, null);
    }

    return (
        <Drawer
            anchor='right'
            open={isOpen}
            handleOnClose={handleAfterSave}
            onCancel={handleAfterSave}
            headerTitle={drawerTitle}
            renderFooter={CreateBinderFooter}
            isLoading={false}
        >
            <div style={{ display: `${isStep1 ? 'block' : 'none'}` }}>
                <BasicInformation
                    onChange={handleOnChange}
                    isStudyBinder={isStudyBinder}
                    notValidFields={notValidsFields}
                    isDelegationOfAuthorityOn={isDelegationOfAuthorityOn}
                    isEventOn={isEventOn}
                    onDelegationCheck={handleDOACheck}
                    onEventCheck={handleEventCheck}
                    handleDetailsChange={setStudyDetails}
                    handleEssetialsChange={setStudyEssentials}
                />
            </div>
            <div style={{ display: `${isStep2 ? 'block' : 'none'}` }}>
                {
                    restOfStudyDetails.length > 0 && <BinderDetails
                        data={restOfStudyDetails}
                        dataSources={dataSources}
                        canAddDataSource={canAddDataSource}
                        overrides={selectedDatasourceOverrides}
                        binderDetails={studyDetails}
                        essentials={binderEssentials}
                        handleDetailsChange={setStudyDetails}
                        handleEssetialsChange={setStudyEssentials}
                        study={study}
                    />
                }

            </div>
        </Drawer>
    );
}

export default connect(
    (state, props) => {
        const institutionId = get(state, 'me.institution.id');
        const canAddDataSource = get(state, `me.institutionPermissions.${institutionId}.dataSources`, false);
        const binderTemplates = get(state, 'me.binderTemplates', []);
        const studyStages = map(get(state, 'study.studyStages', []), (stage) => ({ ...stage, value: stage.name, label: stage.name }));
        const binderTypes = map(get(state, 'study.usableStudyTypes', []), (type) => ({ ...type, value: type.name, label: type.name }));
        const study = get(state, 'study', {});
        return {
            institutionId,
            binderTypes,
            studyStages,
            binderTemplates,
            dataSources: filter(state.dataSources, ['institutionId', institutionId]),
            selectedDatasourceOverrides: get(state, 'selectedDatasourceOverrides', []),
            studyDetails: get(state, 'study.studyDetails', []),
            dataType: dataSourceDataTypeSelector(state),
            study,
            canAddDataSource,
            isDraft: get(state, 'study.isDraft', false),
        };
    }, {
        getUsableStudyTypesByInstitution,
        loadDataSourceList,
        getDatasourceOverrides,
        getStudyMetadata,
        getStudyStages,
        saveStudy,
        initializeStudyWorkflow,
        setField,
        getStudy,
        loadStudy,
        unloadStudy,
        updateStudy,
        updateDraft,
    }
)(CreateBinder);